import { getJobStatus } from '~api/ai/getJobStatus';
import type { AiJobStatusResponse } from '~api/ai/getJobStatus';

import { GenerateAIQuestionsEventAdapter } from '~analytics/adapters/GenerateAIQuestionsEventAdapter';
import type { Poll, PollConfig, PollResult } from '~shared/polling';
import { PollStatus } from '~shared/polling';

import { AIVersions } from '../../../../../shared/config/utils/constants';

interface QuestionsMetadata {
  requestId: string;
  context?: 'generate' | 'import';
  quizId?: number;
  socNumber?: string;
  workspaceId?: string;
  workspaceQuizId?: string;
  analyticsMetadata?: {
    numQuestions: number;
    multipleChoice: boolean;
    trueFalse: boolean;
    shortAnswer: boolean;
    includeExplanation: boolean;
    promptLength: number;
    fileUploaded: boolean;
    fileUploadType: string | null;
    fileUploadSizeBytes: number | null;
    filesUploadedCount: number;
  };
}

export const questionsPollerConfig: PollConfig<
  AiJobStatusResponse,
  QuestionsMetadata
> = {
  type: 'ai-questions',

  pollFn: async (
    poll: Poll<AiJobStatusResponse, QuestionsMetadata>
  ): Promise<PollResult<AiJobStatusResponse>> => {
    const { requestId } = poll.metadata as unknown as QuestionsMetadata;

    const jobStatus = await getJobStatus(requestId);

    if (jobStatus.status === 'completed') {
      return {
        status: PollStatus.Completed,
        data: jobStatus,
        shouldContinuePolling: false,
      };
    } else if (jobStatus.status === 'failed') {
      return {
        status: PollStatus.Failed,
        data: jobStatus,
        shouldContinuePolling: false,
        error: jobStatus.error || 'Question generation failed',
      };
    } else {
      return {
        status: PollStatus.Pending,
        data: jobStatus,
        shouldContinuePolling: true,
      };
    }
  },

  onComplete: (poll, result) => {
    console.debug('[Questions Poller] Polling completed!', poll, result);
    if (
      result.status === PollStatus.Completed &&
      result.data?.data?.questions
    ) {
      const { analyticsMetadata } =
        poll.metadata as unknown as QuestionsMetadata;

      // Send analytics for async file-based generation
      if (analyticsMetadata) {
        new GenerateAIQuestionsEventAdapter({
          ...analyticsMetadata,
          version: AIVersions.GPT_5_Mini,
          promptVersion: result.data.data.promptVersion || 'unknown',
          responseMilliseconds: result.data.data.elapsedTime || 0,
          promptTokens: result.data.data.usage?.promptTokens || 0,
          completionTokens: result.data.data.usage?.completionTokens || 0,
          totalTokens: result.data.data.usage?.totalTokens || 0,
        }).sendAnalytics();
      }
    }
  },

  onFailure: (poll, error) => {
    console.debug('[Questions Poller] Polling failed', poll, error);
  },

  onTimeout: (poll) => {
    console.debug('[Questions Poller] Polling timed out', poll);
  },

  pollingStrategy: {
    initialDelayMs: 5000,
    fastPollingInterval: 5000,
    fastPollingDuration: 2 * 60 * 1000,
    maxInterval: 30000,
    maxDuration: 7 * 60 * 1000,
    initialInterval: 5000,
    useExponentialBackoff: false,
  },
};
